/**
 * Utility functions for comparing values and objects when detecting changes in
 * form state.
 *
 * Two flavours live here, and which one you want depends on what "unchanged"
 * means for your form. `isEmptyValue`/`areValuesEqual`/`hasObjectChanges` treat
 * the empty values (null, undefined, '', [], false) as equivalent to each
 * other. `isDeepEqualIgnoringListOrder` keeps empty values distinct but treats
 * a reordered list of primitives as unchanged.
 */

import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import union from 'lodash/union';

/**
 * Checks if a value is considered "empty" for filter comparison.
 * Empty values: null, undefined, empty string, empty array, or false.
 *
 * Note: We use a custom function instead of lodash's `isEmpty` because:
 * - lodash treats ALL numbers as empty (we want `0` to be valid)
 * - lodash treats ALL booleans as empty (we want `true` to be valid)
 * - We want `false` to be empty (unchecked checkbox = no filter)
 *
 * @param value - The value to check
 * @returns True if the value is empty, false otherwise
 *
 * @example
 * isEmptyValue(null)      // true
 * isEmptyValue(undefined) // true
 * isEmptyValue('')        // true
 * isEmptyValue([])        // true
 * isEmptyValue(false)     // true (unchecked checkbox = no filter)
 * isEmptyValue('test')    // false
 * isEmptyValue([1, 2])    // false
 * isEmptyValue(0)         // false (0 is a valid value)
 * isEmptyValue(true)      // false (checked checkbox = filter active)
 */
export function isEmptyValue(value: unknown): boolean {
  if (isNil(value) || value === '' || value === false) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * Compares two values for equality, treating empty values as equivalent.
 * Uses lodash's `isEqual` for deep comparison of non-empty values.
 *
 * @param current - The current value
 * @param initial - The initial/original value
 * @returns True if values are considered equal, false otherwise
 *
 * @example
 * areValuesEqual('', undefined)     // true (both empty)
 * areValuesEqual([], null)          // true (both empty)
 * areValuesEqual('test', 'test')    // true (same value)
 * areValuesEqual([1, 2], [1, 2])    // true (same array)
 * areValuesEqual('test', '')        // false (one has value)
 * areValuesEqual([1], [2])          // false (different arrays)
 */
export function areValuesEqual(current: unknown, initial: unknown): boolean {
  const currentEmpty = isEmptyValue(current);
  const initialEmpty = isEmptyValue(initial);

  // Both empty = equal
  if (currentEmpty && initialEmpty) return true;

  // One empty, one not = not equal
  if (currentEmpty !== initialEmpty) return false;

  // Both have values - use lodash deep comparison
  return isEqual(current, initial);
}

/**
 * Compares two objects to detect if any values have changed.
 * Treats empty values (null, undefined, '', [], false) as equivalent.
 *
 * @param current - The current object state
 * @param initial - The initial/original object state
 * @returns True if any values have changed, false otherwise
 *
 * @example
 * // No changes (empty values are equivalent)
 * hasObjectChanges({ from: '' }, {})                    // false
 * hasObjectChanges({ items: [] }, { items: undefined }) // false
 *
 * // Changes detected
 * hasObjectChanges({ from: 'test' }, {})                // true
 * hasObjectChanges({ from: 'a' }, { from: 'b' })        // true
 */
export function hasObjectChanges<T extends object>(
  current: T,
  initial: T,
): boolean {
  const currentObj = current as Record<string, unknown>;
  const initialObj = initial as Record<string, unknown>;

  // Get all unique keys from both objects using lodash union
  const allKeys = union(Object.keys(currentObj), Object.keys(initialObj));

  // Check if any key has a meaningful difference
  return allKeys.some(key => !areValuesEqual(currentObj[key], initialObj[key]));
}

/**
 * Structural equality over plain JSON shapes — numbers, booleans, strings,
 * arrays of those, and string-keyed objects — where a list of primitives is
 * compared as a set rather than a sequence.
 *
 * The order-insensitivity is the whole point. Reach for this when the values
 * came from a set-like source (a Rust `HashSet`/`BTreeSet` on the wire, a
 * multi-select, a tag input) and a reordered list genuinely is the same value:
 * counting the reorder as a change means showing a bogus "modified" state and
 * writing back a redundant key. Use `areValuesEqual` when list order matters,
 * or when the empty values should collapse into each other.
 *
 * Lists holding objects still compare position by position. There's no stable
 * identity to pair them up by, so matching them as a set would be guesswork.
 *
 * @param a - The first value
 * @param b - The second value
 * @returns True if the values are structurally equal, ignoring list order
 *
 * @example
 * isDeepEqualIgnoringListOrder(['a', 'b'], ['b', 'a'])   // true (same set)
 * isDeepEqualIgnoringListOrder(['a'], ['a', 'b'])        // false (membership differs)
 * isDeepEqualIgnoringListOrder({ x: 1, y: 2 }, { y: 2, x: 1 }) // true (key order is not order)
 * isDeepEqualIgnoringListOrder([{ id: 1 }, { id: 2 }], [{ id: 2 }, { id: 1 }]) // false (objects are positional)
 * isDeepEqualIgnoringListOrder(null, undefined)          // false (unlike areValuesEqual)
 */
export function isDeepEqualIgnoringListOrder(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;

  // A list and a plain object are never the same value, and without this they
  // could be: `Object.keys([])` is `[]`, so `[]` and `{}` would both fall into
  // the object branch with nothing to disagree about.
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const isPrimitiveList = [...a, ...b].every(
      item => item === null || typeof item !== 'object',
    );
    if (isPrimitiveList) {
      const sortedKeys = (items: unknown[]) =>
        items.map(item => JSON.stringify(item)).sort();
      const left = sortedKeys(a);
      const right = sortedKeys(b);
      return left.every((item, index) => item === right[index]);
    }
    return a.every((item, index) =>
      isDeepEqualIgnoringListOrder(item, b[index]),
    );
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const left = a as Record<string, unknown>;
    const right = b as Record<string, unknown>;
    const leftKeys = Object.keys(left);
    if (leftKeys.length !== Object.keys(right).length) return false;
    return leftKeys.every(
      key =>
        key in right && isDeepEqualIgnoringListOrder(left[key], right[key]),
    );
  }

  return false;
}
